import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { WESTGARD_RULE_WORDS } from '@wodi/domain';
import {
  QC_OUTCOME_WORDS,
  QC_SERIES_STATE_WORDS,
  type QcRunView,
  type QcSeriesListView,
  type QcSeriesRowView,
  type QcSeriesState,
  type QcSeriesView,
} from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Dialog,
  DialogActions,
  EmptyState,
  LeveyJenningsChart,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  TextField,
  type TagState,
} from '@wodi/ui';
import { useState } from 'react';

import {
  addQcCorrectiveAction,
  decideQcOverride,
  qcKeys,
  qcRunInError,
  qcSeriesListQuery,
  qcSeriesQuery,
  recordQcRun,
  requestQcOverride,
} from '../api/lab-qc.ts';
import { labKeys } from '../api/lab.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { formatDateOnly, formatInstant } from '../patients/format.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import styles from './lab.module.css';

// Lab quality control (docs/plans/phase3-lab-qc-and-microbiology.md §5): the series at this
// facility with their state and the QC holds, recording a run, and one series with its
// Levey-Jennings chart, runs, corrective actions and overrides decided by a second person.

const STATE_TAGS: Record<QcSeriesState, TagState> = {
  no_runs: 'neutral',
  accepted: 'done',
  warning: 'attention',
  rejected: 'failed',
  overridden: 'pending',
};

const OUTCOME_TAGS: Record<QcRunView['outcome'], TagState> = {
  accepted: 'done',
  warning: 'attention',
  rejected: 'failed',
};

const rulesText = (rules: readonly string[]) => rules.join(', ');

function seriesName(row: Pick<QcSeriesRowView, 'analyte' | 'material' | 'lot' | 'analyzer'>) {
  return `${row.analyte.name}, ${row.material.name} ${row.material.levelLabel}, lot ${row.lot.lotNumber}, ${row.analyzer?.name ?? 'no analyzer'}`;
}

function invalidateQc(queryClient: ReturnType<typeof useQueryClient>) {
  // A run changes holds, and holds show on the sample pages too.
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: qcKeys.all }),
    queryClient.invalidateQueries({ queryKey: labKeys.work }),
  ]);
}

function NoAccess({ title }: { title: string }) {
  return (
    <>
      <PageHeader title={title} />
      <Notice tone="info">
        You don't have permission to see lab quality control. Ask an administrator if you need it.
      </Notice>
    </>
  );
}

function LoadError({ error, retry, what }: { error: unknown; retry: () => void; what: string }) {
  return (
    <Notice
      tone="error"
      action={
        <Button dense onPress={retry}>
          Try again
        </Button>
      }
    >
      {errorText(error, `The ${what} couldn't be loaded. Try again.`)}
    </Notice>
  );
}

/** Lab > Quality control: every series, its last run and state, and the holds. */
export function LabQcPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const list = useQuery(qcSeriesListQuery);
  const online = useOnline();
  const [recording, setRecording] = useState<string | null | false>(false);
  if (session === null || !can(session, 'lab.qc.read')) return <NoAccess title="Quality control" />;
  const canRecord = can(session, 'lab.qc.record');
  return (
    <div className={styles.page}>
      <p className={styles.meta}>
        <Link to="/lab">Back to the lab worklist</Link>
      </p>
      <PageHeader
        title="Quality control"
        actions={
          canRecord ? (
            <Button
              variant="primary"
              isDisabled={!online || (list.data?.items.length ?? 0) === 0}
              onPress={() => {
                setRecording(null);
              }}
            >
              Record QC run
            </Button>
          ) : null
        }
      />
      {!online ? (
        <Notice tone="info">Offline. Showing what was last loaded; runs can't be recorded.</Notice>
      ) : null}
      {list.isPending ? <LoadingBlock label="Loading QC" rows={4} /> : null}
      {list.isError ? (
        <LoadError error={list.error} retry={() => void list.refetch()} what="QC series" />
      ) : null}
      {list.data === undefined ? null : <HoldsNotice list={list.data} />}
      {list.data?.items.length === 0 ? (
        <EmptyState
          action={
            can(session, 'lab.qc.manage') ? (
              <Link to="/settings/lab/qc">Set up QC materials and targets</Link>
            ) : undefined
          }
        >
          No QC targets are set at this facility yet.
        </EmptyState>
      ) : null}
      {list.data !== undefined && list.data.items.length > 0 ? (
        <TableScroll>
          <Table aria-label="QC series" dense>
            <TableHeader>
              <Column isRowHeader>Analyte</Column>
              <Column>Control</Column>
              <Column>Lot</Column>
              <Column>Analyzer</Column>
              <Column>Last run</Column>
              <Column numeric>SD index</Column>
              <Column>State</Column>
              <Column>Action</Column>
            </TableHeader>
            <TableBody>
              {list.data.items.map((row) => (
                <Row key={row.targetId}>
                  <Cell>
                    <Link to="/lab/qc/$targetId" params={{ targetId: row.targetId }}>
                      {row.analyte.name}
                    </Link>
                  </Cell>
                  <Cell>{`${row.material.name}, ${row.material.levelLabel}`}</Cell>
                  <Cell>
                    <span className="wodi-id">{row.lot.lotNumber}</span>
                    {row.lot.expired ? ' (expired)' : ''}
                  </Cell>
                  <Cell>{row.analyzer?.name ?? 'No analyzer'}</Cell>
                  <Cell>
                    {row.lastRun === null
                      ? 'None'
                      : `${row.lastRun.value} at ${formatInstant(row.lastRun.runAt)}`}
                  </Cell>
                  <Cell numeric>{row.lastRun?.sdIndex ?? ''}</Cell>
                  <Cell>
                    <Tag state={STATE_TAGS[row.state]}>
                      {row.overrideRequested
                        ? 'Override requested'
                        : QC_SERIES_STATE_WORDS[row.state]}
                    </Tag>
                  </Cell>
                  <Cell>
                    {canRecord ? (
                      <Button
                        dense
                        isDisabled={!online}
                        aria-label={`Record a run for ${seriesName(row)}`}
                        onPress={() => {
                          setRecording(row.targetId);
                        }}
                      >
                        Record run
                      </Button>
                    ) : null}
                  </Cell>
                </Row>
              ))}
            </TableBody>
          </Table>
        </TableScroll>
      ) : null}
      {recording === false || list.data === undefined ? null : (
        <RecordRunDialog
          items={list.data.items}
          targetId={recording}
          onClose={() => {
            setRecording(false);
          }}
        />
      )}
    </div>
  );
}

function HoldsNotice({ list }: { list: QcSeriesListView }) {
  if (list.holds.length === 0) return null;
  return (
    <Notice tone="error">
      <span>
        QC holds patient results for these analytes. They can't be validated until an acceptable
        run, or an override approved by a second person.
      </span>
      <ul className={styles.list}>
        {list.holds.map((hold) => (
          <li key={`${hold.analyte.id}-${hold.analyzer?.id ?? 'none'}`}>
            {`${hold.analyte.name} on ${hold.analyzer?.name ?? 'no analyzer (by hand)'}: `}
            {hold.runs.map((run, i) => (
              <span key={run.id}>
                {i === 0 ? '' : ', '}
                <Link to="/lab/qc/$targetId" params={{ targetId: run.targetId }}>
                  {`${run.levelLabel}, lot ${run.lotNumber}`}
                </Link>
              </span>
            ))}
          </li>
        ))}
      </ul>
    </Notice>
  );
}

function RecordRunDialog({
  items,
  targetId,
  onClose,
}: {
  items: readonly QcSeriesRowView[];
  targetId: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<string | null>(targetId);
  const [value, setValue] = useState('');
  const [comment, setComment] = useState('');
  const [tried, setTried] = useState(false);
  const row = items.find((i) => i.targetId === chosen);
  const record = useMutation({
    mutationFn: () =>
      recordQcRun({
        targetId: chosen ?? '',
        value: value.trim(),
        comment: comment.trim() === '' ? null : comment.trim(),
      }),
    onSuccess: async (series) => {
      const run = series.runs.at(-1);
      toasts.show({
        title: 'QC run recorded',
        ...(run === undefined
          ? {}
          : {
              description: `${QC_OUTCOME_WORDS[run.outcome]}${run.violations.length === 0 ? '' : `: ${rulesText(run.violations)}`}`,
            }),
      });
      await invalidateQc(queryClient);
      onClose();
    },
  });
  return (
    <Dialog
      title="Record QC run"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          setTried(true);
          if (chosen !== null && value.trim() !== '') record.mutate();
        }}
      >
        <Select
          label="Series"
          options={items.map((i) => ({ id: i.targetId, label: seriesName(i) }))}
          value={chosen}
          onChange={(v) => {
            setChosen(v === null ? null : String(v));
          }}
          {...(tried && chosen === null ? { errorMessage: 'Choose the control and lot.' } : {})}
        />
        {row?.lot.expired === true ? (
          <Notice tone="info">This lot is past its expiry day. Check before using it.</Notice>
        ) : null}
        <TextField
          label="Value"
          {...(row?.analyte.unit == null ? {} : { unit: row.analyte.unit })}
          value={value}
          onChange={setValue}
          autoFocus
          {...(tried && value.trim() === '' ? { errorMessage: 'Enter the measured value.' } : {})}
        />
        <TextArea label="Comment (optional)" value={comment} onChange={setComment} rows={2} />
        {record.isError ? (
          <Notice tone="error">{errorText(record.error, "The run wasn't recorded.")}</Notice>
        ) : null}
        <DialogActions>
          <Button variant="quiet" onPress={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isPending={record.isPending}
            pendingLabel="Recording…"
          >
            Record run
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

const seriesRoute = getRouteApi('/_app/lab/qc/$targetId');

/** One series: its chart, its runs and what was done about them. */
export function LabQcSeriesPage() {
  const { targetId } = seriesRoute.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const series = useQuery(qcSeriesQuery(targetId));
  const online = useOnline();
  const [recording, setRecording] = useState(false);
  if (session === null || !can(session, 'lab.qc.read')) return <NoAccess title="QC series" />;
  if (series.isPending) return <LoadingBlock label="Loading the QC series" rows={6} />;
  if (series.isError)
    return (
      <>
        <PageHeader title="QC series" />
        <LoadError error={series.error} retry={() => void series.refetch()} what="QC series" />
      </>
    );
  const s = series.data;
  const unit = s.target.analyte.unit === null ? '' : ` ${s.target.analyte.unit}`;
  const title = `${s.target.analyte.name}, ${s.material.name} ${s.material.levelLabel}`;
  const valid = s.runs.filter((r) => r.status === 'entered');
  return (
    <div className={styles.page}>
      <p className={styles.meta}>
        <Link to="/lab/qc">All QC series</Link>
      </p>
      <PageHeader
        title={title}
        actions={
          can(session, 'lab.qc.record') && s.target.status === 'active' ? (
            <Button
              variant="primary"
              isDisabled={!online}
              onPress={() => {
                setRecording(true);
              }}
            >
              Record QC run
            </Button>
          ) : null
        }
      />
      {s.held ? (
        <Notice tone="error">
          The latest run of this lot was rejected. Patient results for {s.target.analyte.name} on{' '}
          {s.target.analyzer?.name ?? 'no analyzer'} can't be validated until an acceptable run, or
          an override approved by a second person.
        </Notice>
      ) : null}
      {s.target.status === 'retired' ? (
        <Notice tone="info">
          This target was replaced. Runs are recorded against the current one.
        </Notice>
      ) : null}
      <dl className={styles.factGrid}>
        <div>
          <dt className={styles.meta}>Lot</dt>
          <dd>
            <span className="wodi-id">{s.lot.lotNumber}</span>
            {`, expires ${formatDateOnly(`${s.lot.expiresOn}T12:00:00Z`)}${s.lot.expired ? ' (expired)' : ''}`}
          </dd>
        </div>
        <div>
          <dt className={styles.meta}>Analyzer</dt>
          <dd>{s.target.analyzer?.name ?? 'No analyzer (bench or by hand)'}</dd>
        </div>
        <div>
          <dt className={styles.meta}>Target, as entered by the lab</dt>
          <dd>{`Mean ${s.target.mean}${unit}, SD ${s.target.sd}. ${s.target.source}`}</dd>
        </div>
        <div>
          <dt className={styles.meta}>Observed (for information)</dt>
          <dd>
            {s.statistics.n === 0
              ? 'No runs yet'
              : `${String(s.statistics.n)} runs, mean ${s.statistics.mean ?? ''}, SD ${s.statistics.sd ?? 'not enough runs'}${s.statistics.cvPercent === null ? '' : `, CV ${s.statistics.cvPercent}%`}`}
          </dd>
        </div>
        <div>
          <dt className={styles.meta}>Rules in use</dt>
          <dd>{s.rules.length === 0 ? 'None' : rulesText(s.rules)}</dd>
        </div>
      </dl>
      <Section title="Levey-Jennings chart">
        <LeveyJenningsChart
          title={`${title}, lot ${s.lot.lotNumber}`}
          targetText={`Mean ${s.target.mean}, SD ${s.target.sd}${unit}`}
          points={valid.map((r) => ({
            id: r.id,
            label: formatInstant(r.runAt),
            sdIndex: r.sdIndexHundredths / 100,
            sdIndexText: r.sdIndex,
            valueText: `${r.value}${unit}`,
            outcome: r.outcome,
            outcomeText: QC_OUTCOME_WORDS[r.outcome],
            rulesText: rulesText(r.violations),
          }))}
        />
      </Section>
      <Section title="Runs">
        {s.runs.length === 0 ? <p>No runs recorded for this lot yet.</p> : <RunsTable series={s} />}
        <p className={styles.meta}>
          {s.rules.map((r) => `${r}: ${WESTGARD_RULE_WORDS[r]}`).join('. ')}
        </p>
      </Section>
      {recording ? (
        <RecordRunDialog
          items={[
            {
              targetId: s.target.id,
              analyte: {
                id: s.target.analyte.id,
                name: s.target.analyte.name,
                unit: s.target.analyte.unit,
              },
              material: s.material,
              lot: {
                id: s.lot.id,
                lotNumber: s.lot.lotNumber,
                expiresOn: s.lot.expiresOn,
                expired: s.lot.expired,
              },
              analyzer: s.target.analyzer,
              lastRun: null,
              state: 'no_runs',
              overrideRequested: false,
            },
          ]}
          targetId={s.target.id}
          onClose={() => {
            setRecording(false);
          }}
        />
      ) : null}
    </div>
  );
}

function RunsTable({ series }: { series: QcSeriesView }) {
  const newestFirst = [...series.runs].reverse();
  return (
    <TableScroll>
      <Table aria-label="QC runs, newest first" dense>
        <TableHeader>
          <Column isRowHeader>Measured</Column>
          <Column numeric>Value</Column>
          <Column numeric>SD index</Column>
          <Column>Outcome</Column>
          <Column>Rules</Column>
          <Column>Source</Column>
          <Column>Actions</Column>
        </TableHeader>
        <TableBody>
          {newestFirst.map((run) => (
            <Row key={run.id}>
              <Cell>{formatInstant(run.runAt)}</Cell>
              <Cell numeric>{run.status === 'entered' ? run.value : <s>{run.value}</s>}</Cell>
              <Cell numeric>{run.sdIndex}</Cell>
              <Cell>
                {run.status === 'entered_in_error' ? (
                  <Tag state="neutral">Entered in error</Tag>
                ) : (
                  <Tag state={OUTCOME_TAGS[run.outcome]}>{QC_OUTCOME_WORDS[run.outcome]}</Tag>
                )}
              </Cell>
              <Cell>{rulesText(run.violations)}</Cell>
              <Cell>
                {run.source === 'analyzer' ? 'Analyzer' : `By hand, ${run.enteredBy.displayName}`}
              </Cell>
              <Cell>
                <RunActions
                  run={run}
                  latest={run.id === series.runs.filter((r) => r.status === 'entered').at(-1)?.id}
                />
              </Cell>
            </Row>
          ))}
        </TableBody>
      </Table>
    </TableScroll>
  );
}

function RunActions({ run, latest }: { run: QcRunView; latest: boolean }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const online = useOnline();
  const [open, setOpen] = useState<'action' | 'override' | 'error' | null>(null);
  const done = async (title: string) => {
    toasts.show({ title });
    setOpen(null);
    await invalidateQc(queryClient);
  };
  const action = useMutation({
    mutationFn: (note: string) => addQcCorrectiveAction(run.id, note),
    onSuccess: () => done('Corrective action recorded'),
  });
  const override = useMutation({
    mutationFn: (reason: string) => requestQcOverride(run.id, reason),
    onSuccess: () => done('Override requested'),
  });
  const inError = useMutation({
    mutationFn: (reason: string) => qcRunInError(run.id, reason),
    onSuccess: () => done('Run marked entered in error'),
  });
  const decide = useMutation({
    mutationFn: (input: { id: string; decision: 'approve' | 'decline' }) =>
      decideQcOverride(input.id, input.decision, null),
    onSuccess: (_s, input) =>
      done(input.decision === 'approve' ? 'Override approved' : 'Override declined'),
  });
  if (session === null) return null;
  const pending = run.overrides.find((o) => o.status === 'requested');
  const approved = run.overrides.find((o) => o.status === 'approved');
  const canRecord = can(session, 'lab.qc.record');
  const canOverride = can(session, 'lab.qc.override');
  const live = run.status === 'entered';
  return (
    <div className={styles.stack}>
      {run.correctiveActions.map((a) => (
        <span key={a.id} className={styles.meta}>
          {`${formatInstant(a.takenAt)}, ${a.recordedBy.displayName}: ${a.note}`}
        </span>
      ))}
      {run.statusReason === null ? null : (
        <span className={styles.meta}>{`Entered in error: ${run.statusReason}`}</span>
      )}
      {approved === undefined ? null : (
        <span className={styles.meta}>
          {`Override approved by ${approved.decidedBy?.displayName ?? ''}. Asked by ${approved.requestedBy.displayName}: ${approved.reason}`}
        </span>
      )}
      {pending === undefined ? null : (
        <span className={styles.meta}>
          {`Override requested by ${pending.requestedBy.displayName}: ${pending.reason}`}
        </span>
      )}
      <div className={styles.actions}>
        {canRecord && live ? (
          <Button
            dense
            isDisabled={!online}
            onPress={() => {
              setOpen('action');
            }}
          >
            Add corrective action
          </Button>
        ) : null}
        {canOverride &&
        live &&
        latest &&
        run.outcome === 'rejected' &&
        pending === undefined &&
        approved === undefined ? (
          <Button
            dense
            isDisabled={!online}
            onPress={() => {
              setOpen('override');
            }}
          >
            Request override
          </Button>
        ) : null}
        {canOverride && pending !== undefined ? (
          pending.requestedBy.id === session.user.id ? (
            <span className={styles.meta}>Another person must decide your request.</span>
          ) : (
            <>
              <Button
                dense
                variant="primary"
                isDisabled={!online}
                isPending={decide.isPending}
                pendingLabel="Saving…"
                onPress={() => {
                  decide.mutate({ id: pending.id, decision: 'approve' });
                }}
              >
                Approve override
              </Button>
              <Button
                dense
                isDisabled={!online}
                onPress={() => {
                  decide.mutate({ id: pending.id, decision: 'decline' });
                }}
              >
                Decline override
              </Button>
            </>
          )
        ) : null}
        {canRecord && live ? (
          <Button
            dense
            variant="quiet"
            isDisabled={!online}
            onPress={() => {
              setOpen('error');
            }}
          >
            Entered in error
          </Button>
        ) : null}
      </div>
      {decide.isError ? (
        <Notice tone="error">{errorText(decide.error, "The override wasn't decided.")}</Notice>
      ) : null}
      <ReasonDialog
        title="Add corrective action"
        isOpen={open === 'action'}
        onOpenChange={(o) => {
          setOpen(o ? 'action' : null);
        }}
        confirmLabel="Add corrective action"
        pendingLabel="Adding…"
        isPending={action.isPending}
        error={action.isError ? errorText(action.error, "The action wasn't recorded.") : null}
        onConfirm={(note) => {
          action.mutate(note);
        }}
      >
        <p>What was done about this run, for example recalibrated and reran the control.</p>
      </ReasonDialog>
      <ReasonDialog
        title="Request override"
        isOpen={open === 'override'}
        onOpenChange={(o) => {
          setOpen(o ? 'override' : null);
        }}
        confirmLabel="Request override"
        pendingLabel="Requesting…"
        isPending={override.isPending}
        error={
          override.isError ? errorText(override.error, "The override wasn't requested.") : null
        }
        onConfirm={(reason) => {
          override.mutate(reason);
        }}
      >
        <p>
          Patient results held by this run can be validated once another person with the override
          permission approves. Say why validating now is safe.
        </p>
      </ReasonDialog>
      <ReasonDialog
        title="Mark run entered in error"
        isOpen={open === 'error'}
        onOpenChange={(o) => {
          setOpen(o ? 'error' : null);
        }}
        confirmLabel="Mark entered in error"
        pendingLabel="Marking…"
        isPending={inError.isPending}
        error={inError.isError ? errorText(inError.error, "The run wasn't marked.") : null}
        onConfirm={(reason) => {
          inError.mutate(reason);
        }}
      >
        <p>For a run recorded by mistake. It is kept, never deleted, and leaves the chart.</p>
      </ReasonDialog>
    </div>
  );
}
